import type { SequenceFragment } from '../types';

/**
 * Lookup type for fragments — accepts either an array (of `SequenceFragment`) or a
 * pre-built `Map<string, SequenceFragment>`. Map form avoids O(n) scans inside loops.
 */
export type FragmentLookup =
  | ReadonlyArray<SequenceFragment>
  | ReadonlyMap<string, SequenceFragment>;

const resolveFragment = (
  lookup: FragmentLookup,
  id: string,
): SequenceFragment | undefined => {
  if (lookup instanceof Map) return lookup.get(id);
  return (lookup as ReadonlyArray<SequenceFragment>).find((f) => f.id === id);
};

/**
 * Returns true when `messageFragId` is contained within `ancestorFragId`'s subtree —
 * i.e. either `messageFragId === ancestorFragId` or one of its ancestors via
 * `parentFragmentId`. A `messageFragId` of `undefined` always returns false.
 *
 * Pass a `Map` form of `lookup` (built once outside hot loops) to avoid O(n) scans.
 */
export const isInsideFragmentTree = (
  messageFragId: string | undefined,
  ancestorFragId: string,
  lookup: FragmentLookup,
): boolean => {
  let currentFragId = messageFragId;
  while (currentFragId) {
    if (currentFragId === ancestorFragId) return true;
    const currentFrag = resolveFragment(lookup, currentFragId);
    currentFragId = currentFrag?.parentFragmentId;
  }
  return false;
};

/**
 * Returns true when `candidateId` is a strict descendant of `ancestorId` — does NOT
 * return true if they're equal. Walks up `parentFragmentId` from the candidate.
 */
export const isFragmentDescendantOf = (
  candidateId: string,
  ancestorId: string,
  lookup: FragmentLookup,
): boolean => {
  let currentFrag = resolveFragment(lookup, candidateId);
  while (currentFrag?.parentFragmentId) {
    if (currentFrag.parentFragmentId === ancestorId) return true;
    currentFrag = resolveFragment(lookup, currentFrag.parentFragmentId);
  }
  return false;
};
